import { PagedQuery } from "@rsm-hcd/javascript-core";
import NotebookSortOptions from "models/enumerations/sorting/notebooks/notebook-sort-options";
import NotebookRecord from "models/view-models/notebook-record";
import UserNotebookPermissionsRecord from "models/view-models/notebooks/user-notebook-permissions-record";
import NotebookPermissionRecipientRecord from "models/view-models/notebooks/notebook-permission-recipient-record";
import QueryHookFactory from "utilities/hooks/queries/query-hook-factory";
import SearchQuery from "utilities/interfaces/search-query";
import SortedQuery from "utilities/interfaces/sorted-query";
import ServiceFactory from "utilities/services/service-factory";
import ServiceHookFactory from "utilities/services/service-hook-factory";

export interface NotebookBasePathParams {}

export interface NotebookResourcePathParams {
    id: number;
}

export interface NotebookQueryParams
    extends PagedQuery,
        SearchQuery,
        SortedQuery<NotebookSortOptions> {
    orderBy?: NotebookSortOptions;
    findNewlyShared?: boolean;
}

export interface NotebookQueryParams {
    orderBy?: NotebookSortOptions;
}

export interface NotebookPermissionRecipientsQueryParams {
    searchText?: string;
}

export interface NotebookMyPermissionsQueryParams {
    notebookIds: number[];
}

const baseEndpoint = "notebooks";
const resourceEndpoint = `${baseEndpoint}/:id`;
const recipientsEndpoint = `${baseEndpoint}/recipients`;
const myPermissionsEndpoint = `${baseEndpoint}/permissions`;
const recipientsKey = "notebook-permissions-recipients";
const myPermissionsKey = "my-notebook-permissions";

const resourceType = NotebookRecord;

const NotebookService = {
    create: ServiceFactory.create<NotebookRecord>(resourceType, baseEndpoint),
    useCreate: ServiceHookFactory.useCreate<NotebookRecord>(
        resourceType,
        baseEndpoint
    ),
    list: ServiceFactory.list<NotebookRecord, NotebookQueryParams>(
        resourceType,
        baseEndpoint
    ),
    update: ServiceFactory.update<NotebookRecord, NotebookResourcePathParams>(
        resourceType,
        resourceEndpoint
    ),
    useList: ServiceHookFactory.useNestedList<
        NotebookRecord,
        NotebookBasePathParams,
        NotebookQueryParams
    >(resourceType, baseEndpoint),
    useGet: ServiceHookFactory.useGet<
        NotebookRecord,
        NotebookResourcePathParams
    >(resourceType, resourceEndpoint),
    useUpdate: ServiceHookFactory.useUpdate<
        NotebookRecord,
        NotebookResourcePathParams
    >(resourceType, resourceEndpoint),
    useDelete: ServiceHookFactory.useDelete(resourceEndpoint),

    useRecipientsQuery: QueryHookFactory.useListQuery<
        NotebookPermissionRecipientRecord,
        NotebookPermissionRecipientsQueryParams
    >(recipientsKey, NotebookPermissionRecipientRecord, recipientsEndpoint),

    useUserPermissionsQuery: QueryHookFactory.useGetQuery<
        UserNotebookPermissionsRecord,
        Record<string, never>,
        NotebookMyPermissionsQueryParams
    >(myPermissionsKey, UserNotebookPermissionsRecord, myPermissionsEndpoint),

    useListQuery: QueryHookFactory.useListQuery<
        NotebookRecord,
        NotebookQueryParams
    >("list-notebooks", NotebookRecord, baseEndpoint),
};

export default NotebookService;
